import { Model, attr } from 'redux-orm';
import { createAction, createLoadConst, createUpdateConst } from '../util';

export default class GuestLoans extends Model {
  static get modelName() {
    return 'GuestLoans';
  }

  static get options() {
    return {
      idAttribute: 'number',
    };
  }

  static reducer({ type, data = {} }, GuestLoans) {
    switch (type) {
      case LOAD.ACTION: {
        const loan = GuestLoans.withId(data.loanNumber);
        if (loan) {
          loan.update({ profileLoading: true, profileError: null });
        }
        break;
      }
      case LOAD_HISTORY.ACTION:
      case LOAD_SCHEDULED.ACTION: {
        const loan = GuestLoans.withId(data.loanNumber);
        const isHistory = type === LOAD_HISTORY.ACTION;
        if (loan) {
          loan.update({
            [isHistory ? 'historyLoading' : 'scheduledLoading']: true,
            [isHistory ? 'historyError' : 'scheduledError']: null,
            [isHistory ? 'historyStatus' : 'scheduledStatus']: 'loading',
          });
        } else {
          GuestLoans.create({
            number: data.loanNumber,
            [isHistory ? 'historyLoading' : 'scheduledLoading']: true,
            [isHistory ? 'historyStatus' : 'scheduledStatus']: 'loading',
          });
        }
        break;
      }
      case LOAD.SUCCESS: {
        const loans = Array.isArray(data?.response)
          ? data.response
          : data?.response?.payload?.loans || data?.response?.loans || [];
        const validLoans = loans.filter(
          (loan) =>
            loan?.number !== null &&
            loan?.number !== undefined &&
            loan?.number !== '' &&
            loan?.number !== 0
        );

        if (validLoans.length === 0) {
          GuestLoans.delete();
          break;
        }

        const existingLoans = new Map(
          GuestLoans.all()
            .toModelArray()
            .map((loan) => [String(loan.number), loan])
        );
        const returnedLoanNumbers = new Set(
          validLoans.map((loan) => String(loan.number))
        );

        existingLoans.forEach((loan, number) => {
          if (!returnedLoanNumbers.has(number)) {
            loan.delete();
          }
        });

        validLoans.forEach((r) => {
          const existing = existingLoans.get(String(r.number));
          const next = {
            ...r,
            historyTransactions: existing?.historyTransactions || [],
            historyTotalRecords: existing?.historyTotalRecords,
            historyLoading: existing?.historyLoading,
            historyError: existing?.historyError,
            historyStatus: existing?.historyStatus,
            scheduledTransactions: existing?.scheduledTransactions || [],
            scheduledTotalRecords: existing?.scheduledTotalRecords,
            scheduledLoading: existing?.scheduledLoading,
            scheduledError: existing?.scheduledError,
            scheduledStatus: existing?.scheduledStatus,
            profileLoading: false,
            profileError: null,
          };
          if (existing) existing.update(next);
          else GuestLoans.create(next);
        });
        break;
      }
      case LOAD_HISTORY.SUCCESS: {
        const { loanNumber, transactions, totalRecords } = data;
        const loan = GuestLoans.withId(loanNumber);
        const nextTransactions = Array.isArray(transactions)
          ? transactions
          : [];
        if (loan) {
          loan.update({
            historyTransactions: nextTransactions,
            historyTotalRecords: totalRecords ?? nextTransactions.length,
            historyLoading: false,
            historyError: null,
            historyStatus: 'success',
          });
        } else {
          GuestLoans.create({
            number: loanNumber,
            historyTransactions: nextTransactions,
            historyTotalRecords: totalRecords ?? nextTransactions.length,
            historyLoading: false,
            historyError: null,
            historyStatus: 'success',
          });
        }
        break;
      }
      case LOAD_SCHEDULED.SUCCESS: {
        const { loanNumber, transactions, totalRecords } = data;
        const loan = GuestLoans.withId(loanNumber);
        const nextTransactions = Array.isArray(transactions)
          ? transactions
          : [];
        if (loan) {
          loan.update({
            scheduledTransactions: nextTransactions,
            scheduledTotalRecords: totalRecords ?? nextTransactions.length,
            scheduledLoading: false,
            scheduledError: null,
            scheduledStatus: 'success',
          });
        } else {
          GuestLoans.create({
            number: loanNumber,
            scheduledTransactions: nextTransactions,
            scheduledTotalRecords: totalRecords ?? nextTransactions.length,
            scheduledLoading: false,
            scheduledError: null,
            scheduledStatus: 'success',
          });
        }
        break;
      }
      case CANCEL_GUEST_SCHEDULED.SUCCESS: {
        const { loanNumber, scheduleId } = data;
        const loan = GuestLoans.withId(loanNumber);
        if (loan) {
          // Update the specific transaction status to 'canceled'
          const scheduledTransactions = loan.scheduledTransactions.map((txn) =>
            txn.id === scheduleId ? { ...txn, status: 'canceled' } : txn
          );
          loan.update({ scheduledTransactions });
        }
        break;
      }
      case LOAD.FAILURE: {
        if (data.loanNumber) {
          const existing = GuestLoans.withId(data.loanNumber);
          const next = {
            number: data.loanNumber,
            profileLoading: false,
            profileError: data.error || 'Unable to load guest profile.',
          };
          if (existing) existing.update(next);
          else GuestLoans.create(next);
        } else {
          GuestLoans.delete();
        }
        break;
      }
      case LOAD_HISTORY.FAILURE:
      case LOAD_SCHEDULED.FAILURE: {
        const loan = GuestLoans.withId(data.loanNumber);
        const isHistory = type === LOAD_HISTORY.FAILURE;
        if (loan) {
          loan.update({
            [isHistory ? 'historyLoading' : 'scheduledLoading']: false,
            [isHistory ? 'historyError' : 'scheduledError']:
              data.error || 'Unable to load transactions.',
            [isHistory ? 'historyStatus' : 'scheduledStatus']: 'error',
          });
        }
        break;
      }
      default:
        break;
    }
  }

  /**
   * Declaring all data fields is recommended as the library doesn't have to
   * redefine getters and setters when instantiating Models
   * */

  static get fields() {
    return {
      id: attr(),
      number: attr(),
      loans: attr(),
      loanNumber: attr(),
      type: attr(),
      numberSuffix: attr(),
      status: attr(),
      email: attr(),
      ownerType: attr(),
      sortBy: attr(),
      sortDirection: attr(),
      payers: attr(),
      historyTransactions: attr(),
      historyTotalRecords: attr(),
      historyLoading: attr(),
      historyError: attr(),
      historyStatus: attr(),
      scheduledTransactions: attr(),
      scheduledTotalRecords: attr(),
      scheduledLoading: attr(),
      scheduledError: attr(),
      scheduledStatus: attr(),
      profileLoading: attr(),
      profileError: attr(),
      // Guest transaction history fields
      sendOn: attr(),
      deliverOn: attr(),
      processOn: attr(),
      scheduleSendOn: attr(),
      scheduleDeliverOn: attr(),
      frequency: attr(),
      confirmationNumber: attr(),
      fundingAccount: attr(),
      destination: attr(),
      payerName: attr(),
      payerPhone: attr(),
      recipientName: attr(),
      coreAccountType: attr(),
      coreAccountNumberMask: attr(),
      a2atracenumber: attr(),
      description: attr(),
      termsAndConditionsUrl: attr(),
      createdAt: attr(),
      updatedAt: attr(),
      amount: attr(),
      feeAmount: attr(),
    };
  }
}

const name = GuestLoans.modelName;

export const LOAD = createUpdateConst(name);
export const load = createAction(LOAD);

export const LOAD_ID = createLoadConst(`${name}_id`);
export const loadById = createAction(LOAD_ID);

export const LOAD_HISTORY = createUpdateConst(`${name}_HISTORY`);
export const LOAD_SCHEDULED = createUpdateConst(`${name}_SCHEDULED`);
export const CANCEL_GUEST_SCHEDULED = createUpdateConst(
  `${name}_CANCEL_SCHEDULED`
);

export const loadHistory = createAction(LOAD_HISTORY);
export const loadScheduled = createAction(LOAD_SCHEDULED);
export const cancelGuestScheduled = createAction(CANCEL_GUEST_SCHEDULED);
